import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtUSD, vendorLabel } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { Check } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    (async () => {
      const r = await api.get(`/products/${id}`);
      setProduct(r.data);
      const rel = await api.get(`/products`, { params: { vendor: r.data.vendor } });
      setRelated(rel.data.filter((p) => p.id !== r.data.id).slice(0, 4));
    })();
  }, [id]);

  if (!product) return <div className="max-w-7xl mx-auto p-16 text-white/40">Loading…</div>;

  const hasSale = product.compare_at_price && product.compare_at_price > product.price;
  const pctOff = hasSale ? Math.round((1 - product.price / product.compare_at_price) * 100) : 0;

  return (
    <div data-testid={`pdp-${product.id}`} className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="overline mb-8">
        <Link to={`/c/${product.vendor}`} className="hover:text-white">{vendorLabel(product.vendor)}</Link>
      </div>

      <div className="grid md:grid-cols-2 gap-12 md:gap-20">
        <div className="bg-[#121215] aspect-[4/5] relative overflow-hidden grain">
          <img src={product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover" />
          {hasSale && (
            <div className="absolute top-4 left-4 bg-white text-black text-[11px] font-bold tracking-[0.2em] uppercase px-3 py-1.5">
              {pctOff}% Off
            </div>
          )}
        </div>
        <div className="md:sticky md:top-24 self-start">
          <h1 className="font-serif italic text-5xl md:text-6xl leading-[0.95] tracking-tight">{product.name}</h1>

          <div className="mt-6 flex items-baseline gap-3">
            <div data-testid="pdp-price" className="text-3xl font-serif">{fmtUSD(product.price)}</div>
            {hasSale && (
              <>
                <div data-testid="pdp-compare-price" className="text-lg text-white/40 line-through">{fmtUSD(product.compare_at_price)}</div>
                <div className="overline text-white/80">Save {pctOff}%</div>
              </>
            )}
          </div>

          <p className="mt-8 text-white/70 leading-relaxed">{product.description}</p>

          <ul className="mt-10 space-y-2 text-sm text-white/60">
            <li className="flex items-center gap-3"><Check className="w-4 h-4 text-white" /> Authenticity guaranteed</li>
            <li className="flex items-center gap-3"><Check className="w-4 h-4 text-white" /> Ships within 24 hours</li>
            <li className="flex items-center gap-3"><Check className="w-4 h-4 text-white" /> Receipt + vendor brochure PDF emailed instantly</li>
          </ul>

          <button
            type="button"
            data-testid="add-to-cart-button"
            onClick={() => { add(product, 1); toast.success("Added to bag"); }}
            className="mt-10 w-full py-5 bg-white text-black uppercase tracking-[0.22em] text-xs font-semibold hover:bg-white/90 transition"
          >
            Add to bag — {fmtUSD(product.price)}
          </button>

          <a
            href="https://tiktok.com/@ayush.resellz"
            target="_blank"
            rel="noreferrer"
            data-testid="pdp-tiktok"
            className="mt-3 block w-full py-5 text-center border border-white/20 uppercase tracking-[0.22em] text-xs font-semibold text-white/80 hover:bg-white/5"
          >
            DM @ayush.resellz on TikTok
          </a>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-32">
          <div className="overline mb-6">More from {vendorLabel(product.vendor)}</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12">
            {related.map((p) => (
              <Link key={p.id} to={`/p/${p.id}`} data-testid={`related-${p.id}`} className="group block">
                <div className="bg-[#121215] aspect-[4/5] overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                </div>
                <div className="mt-4 font-serif text-lg truncate">{p.name}</div>
                <div className="text-sm text-white/50">{fmtUSD(p.price)}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
